import React from 'react';
import { FieldType } from 'types/FieldTypes';
import FormModal from 'components/modals/FormModal';
import { useModal } from 'components/modals/ModalContext';
import { useGlobalContext } from 'GlobalContext';
import InvoiceTemplateModal from './InvoiceTemplateModal';
import { INVOICE_INTERVAL_OPTIONS } from './InvoiceScheduleCard';

// Gives the date one month after the given date as iso date string
const addMonth = (dateValue: Date) => {
    const nextDate = new Date(dateValue);
    nextDate.setMonth(nextDate.getMonth() + 1);
    return nextDate.toISOString().split('T')[0];
};

// Form which asks for the rhythm of a periodic invoice. In create
// mode it makes an existing invoice periodic, in edit mode it
// changes the schedule which is given as item id of the modal
const InvoiceScheduleForm: React.FC<{
    viewKey?: string;
    mode?: 'create' | 'edit';
    invoiceId?: number;
    invoiceDate?: string | null;
}> = ({
    viewKey,
    mode = 'create',
    invoiceId,
    invoiceDate,
}) => {
    const { initializeModal } = useModal();
    const { setFloatingAlert } = useGlobalContext();

    // The first new invoice falls one month after the invoice date, or today for a draft
    const defaultFirstDate = invoiceDate ? addMonth(new Date(invoiceDate)) : new Date().toISOString().split('T')[0];

    // Configure the schedule fields
    const fields: FieldType[] = [
        {
            type: 'dropdown',
            name: 'interval_type',
            label: 'contract.job_schedule.interval_type_label',
            disabled_selected: 'invoice.schedule.interval_disabled_selected',
            allowNoneOption: false,
            showSearch: false,
            hardcodedOptions: INVOICE_INTERVAL_OPTIONS,
            ...(mode === 'create' ? { defaultValue: { value: 'monthly' } } : {}),
        },
        {
            type: 'date',
            name: 'first_invoice_date',
            label: 'invoice.schedule.first_invoice_date_label',
            helperText: 'invoice.schedule.first_invoice_date_helper',
            ...(mode === 'create' ? { defaultValue: defaultFirstDate } : {}),
        },
        {
            type: 'date',
            name: 'end_date',
            label: 'invoice.schedule.end_date_label',
            helperText: 'invoice.schedule.end_date_helper',
        },
    ];

    // Open the created periodic invoice after saving
    const handleCreated = (response?: Record<string, any>) => {
        setFloatingAlert({ type: 'success', message: 'validation.invoice_template.created_successfully' });
        if (response?.id) {
            initializeModal(
                <InvoiceTemplateModal itemId={response.id} />,
                { modalSize: 'large', itemId: response.id }
            );
        }
    };

    // The edit mode patches the schedule itself, the modal props carry its id
    if (mode === 'edit') {
        return (
            <FormModal
                viewKey={viewKey}
                apiObject='invoice_schedule'
                fields={fields}
            />
        );
    }

    return (
        <FormModal
            viewKey={viewKey}
            apiObject='invoice_template'
            fields={fields}
            linkedItem={invoiceId ? { source_invoice: invoiceId } : undefined}
            primaryButtonLabel='button.make_invoice_periodic_button_label'
            onSuccess={handleCreated}
        />
    );
};

export default InvoiceScheduleForm;
